/* ============================================================
   Computerspace — TP-Link theme behaviour
   Vanilla JS, no dependencies. Safe to defer.
   Hooks (data-attributes) used:
     [data-cs-add]            <form> add-to-cart (AJAX)
     [data-cs-buy]            Buy-now button (adds, then -> /checkout)
     [data-cs-after-add]      on a form: "drawer" | "cart" | "checkout" (default drawer)
     [data-cs-cart-count]     cart bubble(s) to update
     [data-cs-thumb]          gallery thumbnail (data-img = full src)
     [data-cs-main]           gallery main <img>
     [data-cs-qty]            qty display <span>, with data inc/dec buttons
     [data-cs-qty-inc/dec]    qty steppers
     [data-cs-tab]            tab button (data-tab = panel key)
     [data-cs-panel]          tab panel (data-panel = key)
     [data-cs-faq]            faq question button (toggles parent .is-open)
     [data-cs-pin]            pincode input
     [data-cs-pin-check]      check button
     [data-cs-pin-out]        delivery output element
     [data-cs-sticky]         sticky add-to-cart bar
   ============================================================ */
(function () {
  'use strict';

  /* ---- cart count refresh ---- */
  function refreshCart(count) {
    document.querySelectorAll('[data-cs-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
  }
  function pullCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { refreshCart(c.item_count); return c; })
      .catch(function () {});
  }

  /* ---- open / refresh the theme's cart drawer (best-effort, theme-agnostic) ----
     Strategy:
       1. Dawn & Dawn-derived themes: <cart-drawer> + #cart-icon-bubble. Re-fetch
          those section HTMLs and swap them in, then call drawer.open().
       2. Any theme exposing a <cart-drawer> with an open() method.
       3. Generic: click a cart toggle that opens a drawer.
       4. Fallback: navigate to /cart so the customer always sees their cart.
  */
  function tryDawnDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve(false);
    var url = window.location.pathname + '?sections=cart-drawer,cart-icon-bubble,cart-live-region-text';
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var parser = new DOMParser();
        // swap cart-drawer inner markup
        if (sections['cart-drawer']) {
          var doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          var fresh = doc.querySelector('cart-drawer');
          if (fresh) {
            drawer.innerHTML = fresh.innerHTML;
            drawer.classList.remove('is-empty');
          }
        }
        // swap the header cart bubble
        if (sections['cart-icon-bubble']) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            var bdoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
            var inner = bdoc.querySelector('#cart-icon-bubble') || bdoc.body.firstElementChild;
            if (inner) bubble.innerHTML = inner.innerHTML;
          }
        }
        if (typeof drawer.open === 'function') { drawer.open(); }
        else { drawer.classList.add('active', 'animate', 'drawer--open', 'is-open'); document.body.classList.add('overflow-hidden'); }
        return true;
      })
      .catch(function () { return false; });
  }

  function openCartDrawer() {
    return tryDawnDrawer().then(function (ok) {
      if (ok) return true;
      // generic custom-element with open()
      var d = document.querySelector('cart-drawer, [data-cart-drawer], #CartDrawer, #cart-drawer, .drawer--cart');
      if (d && typeof d.open === 'function') { d.open(); return true; }
      // generic toggle click
      var toggle = document.querySelector('.js-drawer-open-cart, [data-action="open-drawer"], [data-drawer-toggle="cart"], #CartButton, .header__cart-toggle');
      if (toggle) { toggle.click(); return true; }
      return false;
    });
  }

  function handleAfterAdd(form) {
    var mode = (form && form.getAttribute('data-cs-after-add')) || 'drawer';
    if (mode === 'checkout') { window.location.href = '/checkout'; return; }
    if (mode === 'cart') { window.location.href = '/cart'; return; }
    // drawer (default): refresh count, try to open the tray, else fall back to /cart
    pullCart();
    openCartDrawer().then(function (opened) {
      if (!opened) { window.location.href = '/cart'; }
    });
  }

  /* shared add-to-cart; returns the fetch promise */
  function addToCart(form) {
    var data = new FormData(form);
    return fetch('/cart/add.js', {
      method: 'POST',
      body: data,
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) { return r.json().then(function (err) { throw err; }); }
      return r.json();
    });
  }

  /* ---- AJAX add to cart (the normal "Add to cart" button) ---- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-cs-add]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('button[type="submit"], .cs-btn--add, .cs-btn--cart');
    var original = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
    addToCart(form)
      .then(function () {
        if (btn) {
          btn.disabled = false;
          btn.classList.add('is-added');
          btn.textContent = 'Added \u2713';
          setTimeout(function () { btn.classList.remove('is-added'); btn.textContent = original; }, 1800);
        }
        handleAfterAdd(form);
      })
      .catch(function (err) {
        if (btn) { btn.disabled = false; btn.textContent = (err && err.description) ? 'Unavailable' : 'Try again'; setTimeout(function(){ btn.textContent = original; }, 2000); }
      });
  });

  /* ---- Buy now: add the item, then go straight to checkout ---- */
  document.addEventListener('click', function (e) {
    var buy = e.target.closest('[data-cs-buy]');
    if (!buy) return;
    e.preventDefault();
    var form = buy.closest('form[data-cs-add]') || document.querySelector('form[data-cs-add]');
    if (!form) { window.location.href = '/cart'; return; }
    var original = buy.textContent;
    buy.disabled = true; buy.textContent = 'Processing…';
    addToCart(form)
      .then(function () { window.location.href = '/checkout'; })
      .catch(function () { buy.disabled = false; buy.textContent = original; });
  });

  /* ---- gallery ---- */
  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-cs-thumb]');
    if (!thumb) return;
    var scope = thumb.closest('.cs-scope') || document;
    var main = scope.querySelector('[data-cs-main]');
    if (main && thumb.dataset.img) main.src = thumb.dataset.img;
    scope.querySelectorAll('[data-cs-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
    thumb.classList.add('is-active');
  });

  /* ---- qty steppers ---- */
  document.addEventListener('click', function (e) {
    var inc = e.target.closest('[data-cs-qty-inc]');
    var dec = e.target.closest('[data-cs-qty-dec]');
    if (!inc && !dec) return;
    var wrap = (inc || dec).closest('[data-cs-qty-wrap]');
    if (!wrap) return;
    var out = wrap.querySelector('[data-cs-qty]');
    var hidden = wrap.querySelector('input[name="quantity"]');
    var n = parseInt(out.textContent, 10) || 1;
    n = inc ? n + 1 : Math.max(1, n - 1);
    out.textContent = n;
    if (hidden) hidden.value = n;
  });

  /* ---- tabs ---- */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-cs-tab]');
    if (!tab) return;
    var scope = tab.closest('.cs-scope') || document;
    var key = tab.getAttribute('data-tab');
    scope.querySelectorAll('[data-cs-tab]').forEach(function (t) {
      t.classList.toggle('is-active', t.getAttribute('data-tab') === key);
    });
    scope.querySelectorAll('[data-cs-panel]').forEach(function (p) {
      var on = p.getAttribute('data-cs-panel') === key;
      p.classList.toggle('is-active', on);
      /* set inline display too, so no external/app CSS can hide the active panel */
      p.style.display = on ? 'block' : 'none';
    });
    /* nudge Judge.me to lay out its widget once the reviews tab is visible */
    if (key === 'reviews' && window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') {
      try { window.jdgm.batchRenderBadges(); } catch (err) {}
    }
  });

  /* ---- faq accordion ---- */
  document.addEventListener('click', function (e) {
    var q = e.target.closest('[data-cs-faq]');
    if (!q) return;
    var item = q.closest('.cs-faq__item');
    if (item) item.classList.toggle('is-open');
  });

  /* ---- pincode delivery estimate ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cs-pin-check]');
    if (!btn) return;
    var scope = btn.closest('.cs-delivery') || document;
    var input = scope.querySelector('[data-cs-pin]');
    var out = scope.querySelector('[data-cs-pin-out]');
    if (!input || !out) return;
    var pin = (input.value || '').replace(/[^0-9]/g, '').slice(0, 6);
    input.value = pin;
    if (pin.length === 6) {
      var d = new Date();
      d.setDate(d.getDate() + 3);
      var fmt = d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      out.textContent = '\u2713 Delivery by ' + fmt + ' \u00b7 Free shipping';
    } else {
      out.textContent = 'Enter a valid 6-digit pincode';
    }
    out.classList.add('is-on');
  });
  document.addEventListener('input', function (e) {
    var input = e.target.closest('[data-cs-pin]');
    if (!input) return;
    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 6);
  });

  /* ---- sticky add-to-cart bar ---- */
  var sticky = document.querySelector('[data-cs-sticky]');
  if (sticky) {
    var onScroll = function () { sticky.classList.toggle('is-on', window.scrollY > 560); };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* keep cart bubble in sync on load */
  document.addEventListener('DOMContentLoaded', pullCart);
})();
